/**
 * THE FIT RULE AFTER CONTRACT 1.5ac: THE GAME FILLS THE SCREEN AND EVERY ART PIXEL STAYS SQUARE.
 *
 * The owner, on their iPad mini: "I would also like the game to take up the full screen as it seems
 * to be small on the ipad". Their choice, "Fill it, keep pixels crisp", everywhere the game runs.
 *
 * WHAT STANDS FROM 1.5e. The art is authored on the 960x540 grid and is never scaled by a fraction:
 * one art pixel is always a WHOLE NUMBER of DEVICE pixels, `grain`, and the blit is nearest-neighbour.
 *
 * WHAT MOVES. The delivered frame is no longer 960x540 art pixels banded inside the screen. It is
 * however many art pixels the screen holds at the chosen grain, so a 16:9 screen still sees 960x540
 * at grain 2 and a 4:3 iPad sees a little more sea up and down and a little less across. The only
 * band left is the remainder of the division, which is under one art pixel by construction.
 *
 * HOW THE GRAIN IS CHOSEN. The largest whole number that still shows ENOUGH of the world, where
 * "enough" is two named constants, both GUESSES for the owner to judge by eye from per-device
 * screenshots: at least 90% of the authored frame's height and 75% of its width, counted over the
 * whole screen (the sea under a status bar is still seen; see the note in `fitFill`). A bigger
 * grain is a bigger picture and less world; the constants say how little world is too little.
 *
 * `?view=960x540` PINS the view instead (`pin`), and that is the old rule exactly: the largest grain
 * at which the whole pinned frame fits, centred on whole device pixels, with the old CSS floor.
 * Every instrument that measures art or frame keys loads the page that way, so no earlier bar
 * changes what it measures.
 *
 * Pure: three numbers and four insets in, a plan out. Fenced as a table in fill.test.ts.
 */
import { ART_H, ART_W } from './scale';

/** GUESS, for the owner: the view never shows less than this fraction of the authored frame's height. */
export const MIN_VIEW_FRACTION_H = 0.9;
/** GUESS, for the owner: the view never shows less than this fraction of the authored frame's width. */
export const MIN_VIEW_FRACTION_W = 0.75;
/** The two fractions as whole art pixels: 486 rows and 720 columns. */
export const MIN_VIEW_H = Math.ceil(ART_H * MIN_VIEW_FRACTION_H);
export const MIN_VIEW_W = Math.ceil(ART_W * MIN_VIEW_FRACTION_W);

/** An art viewport: whole art columns and rows. */
export interface ArtView {
  readonly w: number;
  readonly h: number;
}

/** The authored frame, which is also what `?view=960x540` pins. */
export const PINNED_VIEW: ArtView = { w: ART_W, h: ART_H };

/** CSS px the operating system reserves at each edge (env(safe-area-inset-*)). */
export interface SafeInsets {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export const NO_INSETS: SafeInsets = { top: 0, right: 0, bottom: 0, left: 0 };

export interface FillBox {
  /** The host element's box in CSS px: the window, less the dev strip when it shows. */
  readonly cssW: number;
  readonly cssH: number;
  /** `window.devicePixelRatio`. Anything not finite and positive is read as 1. */
  readonly dpr: number;
  readonly insets?: SafeInsets;
  /** A pinned art view (`?view=WxH`), or null/undefined to fill. */
  readonly pin?: ArtView | null;
}

export interface FillFit {
  readonly mode: 'fill' | 'pinned';
  /** DEVICE px per art px. A whole number; 0 only when nothing legal fits (`playable` false). */
  readonly grain: number;
  readonly dpr: number;
  /** The art viewport: how many art columns and rows are drawn. */
  readonly view: ArtView;
  /** The box in whole device px. */
  readonly boxDeviceW: number;
  readonly boxDeviceH: number;
  /** The PICTURE in device px: exactly `view * grain`, blitted at the canvas's top-left. */
  readonly deviceW: number;
  readonly deviceH: number;
  /**
   * The canvas's backing store in device px: the picture grown to a whole CSS px (see
   * `wholeCssCanvas`). Equal to the picture when pinned. Never more than the band beyond it.
   */
  readonly canvasDeviceW: number;
  readonly canvasDeviceH: number;
  /** The canvas's laid-out size in CSS px: backing divided by dpr, so nothing rescales it. */
  readonly cssW: number;
  readonly cssH: number;
  /** Where the canvas goes inside the box, CSS px, chosen so both land on whole DEVICE px. */
  readonly left: number;
  readonly top: number;
  /** Device px of the box left uncovered, across and down, in total. Fill: always under `grain`. */
  readonly bandDeviceW: number;
  readonly bandDeviceH: number;
  /** CSS px per art px. */
  readonly apparent: number;
  /** The insets as sanitised, CSS px. */
  readonly insets: SafeInsets;
  /** Art rows/columns under each inset, rounded outward: what the safe area hides of the view. */
  readonly safeArt: SafeInsets;
  readonly playable: boolean;
}

const finitePos = (n: number): number => (Number.isFinite(n) && n > 0 ? n : 0);

/** Parse `?view=` as `WxH` in whole art px. Anything else is null, which means fill. */
export function parseViewParam(value: string | null | undefined): ArtView | null {
  if (typeof value !== 'string') return null;
  const m = /^(\d{2,4})x(\d{2,4})$/.exec(value.trim());
  if (m === null) return null;
  const w = Number(m[1]);
  const h = Number(m[2]);
  return w >= 16 && h >= 16 ? { w, h } : null;
}

/**
 * PUT A LENGTH ON A WHOLE DEVICE PIXEL, because a whole-number SCALE is not enough on its own.
 * Moved here from world-entry.ts unchanged; the measurement behind it is there: at 125% scaling a
 * canvas centred on device y 22.5 delivered 1,043 of 518,400 blocks split across rows, and a
 * near-miss correction (22.0117) was no better. The largest whole CSS px at or under `idealCss`
 * whose product with the ratio is itself a whole number.
 */
export function wholeDevicePx(idealCss: number, dpr: number): number {
  const start = Math.max(0, Math.floor(idealCss));
  for (let cand = start; cand >= 0 && cand > start - 64; cand--) {
    const dev = cand * dpr;
    if (Math.abs(dev - Math.round(dev)) < 1e-9) return cand;
  }
  return start;
}

/**
 * THE CANVAS ELEMENT'S SIZE, IN DEVICE PX: the picture, grown to a WHOLE CSS pixel when it can be.
 *
 * MEASURED in Chromium (tools/lanes/platform-trust/full-screen.test.ts, and seven isolated canvases
 * drawn with a 3 px checker): a canvas whose CSS box is a fraction of a CSS pixel is resampled by
 * the compositor at dpr 2 and 3 - a 2265 device-px picture laid out as 1132.5 CSS px came to the
 * screen 2266 px wide with one column doubled in the middle, 37,988 of 374,480 art-pixel blocks
 * split on the iPad mini in landscape - while the same fractions at dpr 1.25 and 1.5 were exact.
 * So the element is sized to the smallest whole CSS px, whose product with the ratio is a whole
 * device px, that holds the picture and still fits the box; the backing store is that exact size,
 * so nothing rescales, and the picture is blitted at the top-left at `grain` with the remainder
 * (under one art pixel, because the picture already leaves less than that of the box) cleared to
 * the ground inside the canvas. When no whole CSS size fits (a fractional ratio with a box that is
 * not itself a whole number of device px) the canvas is the picture exactly, which is the case the
 * measurement found to be exact.
 */
function wholeCssCanvas(picture: number, box: number, dpr: number): number {
  for (let css = Math.ceil(picture / dpr - 1e-9); css * dpr <= box + 1e-9; css++) {
    const dev = css * dpr;
    if (Math.abs(dev - Math.round(dev)) < 1e-9 && Math.round(dev) >= picture) return Math.round(dev);
  }
  return picture;
}

/** The one place the delivered frame is decided. */
export function fitFill(box: FillBox): FillFit {
  const dpr = Number.isFinite(box.dpr) && box.dpr > 0 ? box.dpr : 1;
  const cssBoxW = finitePos(box.cssW);
  const cssBoxH = finitePos(box.cssH);
  const raw = box.insets ?? NO_INSETS;
  const insets: SafeInsets = {
    top: finitePos(raw.top),
    right: finitePos(raw.right),
    bottom: finitePos(raw.bottom),
    left: finitePos(raw.left),
  };
  // A CSS size times a real ratio can land a hair under the integer it means (1133 * 2 is exact,
  // 1707 * 1.5 is not); the epsilon keeps a representational error from costing a whole device px.
  const boxDeviceW = Math.floor(cssBoxW * dpr + 1e-6);
  const boxDeviceH = Math.floor(cssBoxH * dpr + 1e-6);
  const pin = box.pin ?? null;

  let grain: number;
  let view: ArtView;
  let playable: boolean;
  if (pin !== null) {
    // THE OLD RULE, EXACTLY (scale.ts fitToBox), for any pinned view.
    grain = Math.max(0, Math.floor(Math.min(boxDeviceW / pin.w, boxDeviceH / pin.h)));
    view = { w: pin.w, h: pin.h };
    playable = grain >= 1 && cssBoxW >= pin.w && cssBoxH >= pin.h;
  } else {
    const safeCssW = Math.max(0, cssBoxW - insets.left - insets.right);
    const safeCssH = Math.max(0, cssBoxH - insets.top - insets.bottom);
    // THE WORLD MINIMUM IS COUNTED OVER THE WHOLE SCREEN, NOT THE SAFE AREA, and that is a choice
    // with a measurement behind it. The canvas draws under the insets and that sea is seen: a status
    // bar is a line of small type over it and the home indicator a thin bar. Counted inside the
    // MODELLED iPad insets (24 pt top, 20 pt bottom) the iPad mini in landscape - the owner's own
    // device, the one that "seems to be small" - drops from grain 3 to grain 2, which is exactly the
    // size its art is drawn at today: the screen would fill and nothing would get bigger. The insets
    // still bound the paper shell (world-entry.ts places #game-ui inside them) and the CSS floor.
    grain = Math.max(0, Math.min(Math.floor(boxDeviceW / MIN_VIEW_W), Math.floor(boxDeviceH / MIN_VIEW_H)));
    const g = Math.max(1, grain);
    view = { w: Math.floor(boxDeviceW / g), h: Math.floor(boxDeviceH / g) };
    // The CSS floor is the minimum view at one CSS px per art px, inside the safe area: the paper
    // shell is authored on the same grid at one CSS px per grain px and below this it cannot fit.
    playable = grain >= 1 && safeCssW >= MIN_VIEW_W && safeCssH >= MIN_VIEW_H;
  }
  const deviceW = view.w * grain;
  const deviceH = view.h * grain;
  const canvasDeviceW = pin !== null ? deviceW : wholeCssCanvas(deviceW, boxDeviceW, dpr);
  const canvasDeviceH = pin !== null ? deviceH : wholeCssCanvas(deviceH, boxDeviceH, dpr);
  const cssW = canvasDeviceW / dpr;
  const cssH = canvasDeviceH / dpr;
  const g = Math.max(1, grain);
  return {
    mode: pin !== null ? 'pinned' : 'fill',
    grain,
    dpr,
    view,
    boxDeviceW,
    boxDeviceH,
    deviceW,
    deviceH,
    canvasDeviceW,
    canvasDeviceH,
    cssW,
    cssH,
    left: wholeDevicePx((cssBoxW - cssW) / 2, dpr),
    top: wholeDevicePx((cssBoxH - cssH) / 2, dpr),
    bandDeviceW: Math.max(0, boxDeviceW - deviceW),
    bandDeviceH: Math.max(0, boxDeviceH - deviceH),
    apparent: grain / dpr,
    insets,
    safeArt: {
      top: Math.ceil((insets.top * dpr) / g),
      right: Math.ceil((insets.right * dpr) / g),
      bottom: Math.ceil((insets.bottom * dpr) / g),
      left: Math.ceil((insets.left * dpr) / g),
    },
    playable,
  };
}
